import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useFetch from '../../hooks/useFetch';
import './EventLeaderboard.css';

function PodiumModal({ top3, onClose }) {
  const getOrdinalSuffix = (place) => {
    if (place === 1) return 'st';
    if (place === 2) return 'nd';
    if (place === 3) return 'rd';
    return 'th';
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white p-16 rounded-lg shadow-xl max-w-5xl w-11/12">
        <h2 className="text-6xl font-bold mb-12 text-center">Top 3 Racers</h2>
        <div className="flex justify-between items-end mb-16">
          {[
            {
              place: 2, color: 'text-blue-400', size: 'text-8xl', nameSize: 'text-3xl', emote: '🥈',
            },
            {
              place: 1, color: 'text-yellow-400', size: 'text-9xl', nameSize: 'text-4xl', emote: '🥇',
            },
            {
              place: 3, color: 'text-green-400', size: 'text-7xl', nameSize: 'text-2xl', emote: '🥉',
            },
          ].map(({
            place, color, size, nameSize, emote,
          }) => (
            <div key={place} className="text-center flex flex-col items-center">
              <span className={`${nameSize} font-bold mb-8 max-w-[250px] break-words animate-jump-${place}`}>
                {top3[place - 1].racer_name}
              </span>
              <span className={`${color} ${size} mb-6 animate-jiggle`}>
                {emote}
              </span>
              <span className="text-3xl font-bold">
                {place}{getOrdinalSuffix(place)}
              </span>
            </div>
          ))}
        </div>
        <button
          type="button"
          className="bg-blue-500 hover:bg-blue-700 text-white font-bold py-6 px-10 rounded w-full text-3xl"
          onClick={onClose}
        >
          Close
        </button>
      </div>
    </div>
  );
}

PodiumModal.propTypes = {
  top3: PropTypes.arrayOf(PropTypes.shape({
    racer_name: PropTypes.string.isRequired,
  })).isRequired,
  onClose: PropTypes.func.isRequired,
};

function ViewEventSeriesLeaderboard() {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const {
    fetchData,
    loading,
    error,
    serverError,
  } = useFetch();
  const [leaderboardData, setLeaderboardData] = useState(null);
  const [config, setConfig] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  const [revealedRank, setRevealedRank] = useState(null);
  const [showPodium, setShowPodium] = useState(false);
  const [showBlacklisted, setShowBlacklisted] = useState(false);
  const { type } = location.state || { type: 'series' };

  useEffect(() => {
    const fetchLeaderboard = async () => {
      const endpoint = type === 'collection'
        ? `/api/events/get-collection-leaderboard/${id}`
        : `/api/events/get-series-leaderboard/${id}`;
      const data = await fetchData(endpoint);
      setLeaderboardData(data);
      if (data && data.config) {
        setConfig(JSON.parse(data.config));
      }
    };
    fetchLeaderboard();
  }, [id, fetchData, type]);

  const headers = [
    { key: 'rank', label: 'Rank' },
    { key: 'racer_name', label: 'Racer' },
    { key: 'total_event_points', label: 'Total Points' },
  ];

  if (showDetails) {
    if (config?.gameModes?.racing) {
      headers.push({ key: 'total_race_wins', label: 'Race Wins' });
      headers.push({ key: 'total_race_races', label: 'Races' });
      headers.push({ key: 'max_race_hs', label: 'Best HS' });
    }

    if (config?.gameModes?.battleRoyale) {
      headers.push({ key: 'total_br_wins', label: 'BR Wins' });
      headers.push({ key: 'total_br_races', label: 'BR Matches' });
      headers.push({ key: 'max_br_hs', label: 'Best BR HS' });
      headers.push({ key: 'total_kills', label: 'Kills' });
      headers.push({ key: 'total_eliminations', label: 'Elims' });
      headers.push({ key: 'total_crown_wins', label: 'Crown Wins' });
    }
  }

  const getRevealDuration = (rank) => {
    if (rank > 10) return 1000;
    if (rank > 5) return 1000 + (11 - rank) * 100;
    return 2000 + (6 - rank) * 500;
  };

  const maskName = (name, isRevealed) => {
    if (isRevealed) return name;
    return '•'.repeat(20);
  };

  const handleReveal = () => {
    if (revealedRank === null) {
      setRevealedRank(11); // Reveal all ranks above 10
    } else if (revealedRank > 1) {
      setRevealedRank(revealedRank - 1); // Reveal next rank
    } else if (revealedRank === 1) {
      setShowPodium(true);
    }
  };

  const getRevealButtonText = () => {
    if (revealedRank === null) return 'Start Reveal';
    if (revealedRank === 1) return 'Show Podium';
    return `Reveal Rank ${revealedRank - 1}`;
  };

  const filteredLeaderboard = useMemo(() => {
    if (!leaderboardData) return [];
    if (showBlacklisted) return leaderboardData.leaderboard;

    const blacklist = (config?.blacklist || []).map((name) => name.toLowerCase());
    return leaderboardData.leaderboard
      .filter((entry) => !blacklist.includes(entry.racer_name.toLowerCase()))
      .map((entry, index) => ({ ...entry, rank: index + 1 }));
  }, [leaderboardData, config, showBlacklisted]);

  const top3 = filteredLeaderboard.slice(0, 3);

  if (loading) return <div>Loading...</div>;
  if (serverError) return serverError;
  if (error) return <div>Error: {error.message || error.error}</div>;

  return (
    <div className="p-4">
      <h1 className="text-2xl font-bold mb-4">{type === 'collection' ? 'Collection Leaderboard' : 'Event Series Leaderboard'}</h1>
      <div className="mb-4 flex justify-between">
        <div className="w-fit flex items-center">
          <button
            type="button"
            className="bg-gray-500 hover:bg-gray-700 border border-gray-600 shadow-md text-white font-bold py-3 px-4 rounded mr-2"
            onClick={() => navigate(-1)}
          >
            Back
          </button>
          <button
            type="button"
            className="bg-blue-500 hover:bg-blue-700 border border-blue-600 shadow-md text-white font-bold py-3 px-4 rounded mr-2"
            onClick={() => setShowDetails(!showDetails)}
          >
            {showDetails ? 'Hide Details' : 'Show Details'}
          </button>
          <button
            type="button"
            className="bg-green-500 hover:bg-green-700 border border-green-600 shadow-md text-white font-bold py-3 px-4 rounded"
            onClick={handleReveal}
          >
            {getRevealButtonText()}
          </button>
          {/* eslint-disable-next-line jsx-a11y/label-has-associated-control */}
          <label className="inline-flex items-center text-white font-bold ml-2 bg-gray-500 hover:bg-gray-700 border border-gray-600 shadow-md py-3 px-4 rounded cursor-pointer select-none">
            <input
              type="checkbox"
              checked={showBlacklisted}
              onChange={(e) => setShowBlacklisted(e.target.checked)}
              className="mr-2 h-5 w-5"
            />
            Show Blacklisted
          </label>
        </div>
      </div>
      {leaderboardData && (
      <div className="overflow-x-auto bg-gray-600 border border-1 rounded-md">
        <table className="w-full border-collapse border border-gray-300">
          <thead>
            <tr className="bg-gray-100">
              {headers.map((header) => (
                <th key={header.key} className="border border-gray-300 bg-gray-300 px-4 py-2">{header.label}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filteredLeaderboard.length > 0 ? (
              filteredLeaderboard.map((entry) => {
                const isRevealed = revealedRank === null ? false : entry.rank >= revealedRank;
                return (
                  <tr key={entry.racer_name}>
                    {headers.map((header) => (
                      <td key={`${header.key}-${entry.racer_name}`} className={`border text-white border-gray-300 px-4 py-2 transition-all ease-in-out ${isRevealed ? '' : 'blur-sm'}`} style={{ transitionDuration: `${getRevealDuration(entry.rank)}ms` }}>
                        {header.key === 'racer_name' ? (
                          <div className="flex items-center">
                            <span className="font-mono">{maskName(entry.racer_name, isRevealed)}</span>
                          </div>
                        ) : (
                          entry[header.key]
                        )}
                      </td>
                    ))}
                  </tr>
                );
              })
            ) : (
              <tr>
                <td colSpan={headers.length} className="text-center text-white">No event data for this series yet.</td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
      )}
      {showPodium && <PodiumModal top3={top3} onClose={() => setShowPodium(false)} />}
    </div>
  );
}

export default ViewEventSeriesLeaderboard;
